import React, { useEffect, useState, useContext } from "react";
import {
  Input,
  Select,
  Button,
  Typography,
  Checkbox,
  Tag,
  message,
  Alert,
} from "antd";
import { DownOutlined, RightOutlined, CrownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { PlusOutlined } from "@ant-design/icons";
import Discord from "../assets/discord.svg";
import Telegram from "../assets/telegram.svg";
// [PTG_ONE_LEAGUE_LIST 2026-10-04] was SPORTS_OPTIONS from ../constants/Constant,
// a hand-kept copy. Now built from the shared list at GET /api/leagues.
import { useSportsOptions } from "../constants/leagueOptions";
import { AuthContext } from "../context/authContext";
import UpgradeModal from '../components/modals/UpgradeModal';

const { Option } = Select;

const AddChannelForm = ({
  step,
  channelId,
  setChannelId,
  channelName,
  setChannelName,
  sport,
  setSport,
  addChannel,
  isEditing = false,
}) => {
  const { userData } = useContext(AuthContext);
  // [PTG_ONE_LEAGUE_LIST 2026-10-04] The options now arrive from the server,
  // so the three helpers that read them live inside the component.
  const {
    options: SPORTS_OPTIONS,
    ready: optionsReady,
    error: optionsError,
  } = useSportsOptions();

  const getAllChildOptions = (parentValue) => {
    const parentGroup = SPORTS_OPTIONS.find((opt) => opt.value === parentValue);
    return parentGroup?.children?.map((child) => child.value) || [];
  };

  const getAllLeagueValues = () => {
    return SPORTS_OPTIONS.flatMap((option) =>
      option.children ? option.children.map((child) => child.value) : []
    );
  };

  const getSomeLeaguesSelected = (currentSports) => {
    const allLeagues = getAllLeagueValues();
    const selectedCount = allLeagues.filter((league) =>
      currentSports.includes(league)
    ).length;
    return selectedCount > 0 && selectedCount < allLeagues.length;
  };

  const [expandedParent, setExpandedParent] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentSports = sport ? sport.split(", ") : [];
  const [searchValue, setSearchValue] = useState("");
  const [limitReached, setLimitReached] = useState(false);

  // Get freeAutoSchedule and subscribed from userData
  const freeAutoSchedule =
    typeof userData.freeAutoSchedule === "number"
      ? userData.freeAutoSchedule
      : 0;
  const isSubscribed = userData.subscribed || false;

  // Count of selected sports (leagues)
  const selectedLeaguesCount = currentSports.filter((v) =>
    getAllLeagueValues().includes(v)
  ).length;

  useEffect(() => {
    if (searchValue.trim() === "") {
      setExpandedParent(null);
      return;
    }

    const matchingParents = SPORTS_OPTIONS.filter(
      (option) =>
        option.children &&
        option.children.some((child) =>
          child.label.toLowerCase().includes(searchValue.toLowerCase())
        )
    ).map((option) => option.value);

    if (matchingParents.length > 0) {
      setExpandedParent(matchingParents[0]);
    }
  }, [searchValue]);

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const handleUpgradeClose = () => {
    setShowUpgradeModal(false);
  };

  // Helper to check if adding more is allowed
  const canAddMore = (selectedValues) => {
    if (isSubscribed) return true; // No limit for subscribed users
    // Only count league values (not "All" or parent categories)
    const allLeagues = getAllLeagueValues();
    const leagueSelections = selectedValues.filter((v) =>
      allLeagues.includes(v)
    );
    return leagueSelections.length <= freeAutoSchedule;
  };

  const handleSportChange = (selectedValues) => {
    const allLeagues = getAllLeagueValues();
    const allSelected = allLeagues.every((league) =>
      selectedValues.includes(league)
    );

    if (selectedValues.includes("All") && !allSelected) {
      if (!canAddMore(allLeagues)) {
        setLimitReached(true);
        message.warning(
          `You can only select up to ${freeAutoSchedule} leagues.`
        );
        return;
      }
      setLimitReached(false);
      setSport(allLeagues.join(", "));
      return;
    }

    if (selectedValues.includes("All") && allSelected) {
      setLimitReached(false);
      setSport("");
      return;
    }

    const newAllSelected = allLeagues.every((league) =>
      selectedValues.includes(league)
    );
    let finalValues = [...selectedValues];

    if (newAllSelected && !finalValues.includes("All")) {
      finalValues.push("All");
    } else if (!newAllSelected && finalValues.includes("All")) {
      finalValues = finalValues.filter((v) => v !== "All");
    }

    const parentCategories = SPORTS_OPTIONS.map((opt) => opt.value);
    const filteredValues = finalValues.filter(
      (value) => !parentCategories.includes(value) || value === "All"
    );

    if (!canAddMore(filteredValues)) {
      setLimitReached(true);
      message.warning(`You can only select up to ${freeAutoSchedule} leagues. Please upgrade to Premium for unlimited leagues.`);
      return;
    }

    setLimitReached(false);
    setSport(filteredValues.join(", "));
  };

  const isOptionSelected = (value) => {
    if (value === "All") {
      const allLeagues = getAllLeagueValues();
      return (
        allLeagues.length > 0 &&
        allLeagues.every((league) => currentSports.includes(league))
      );
    }

    if (SPORTS_OPTIONS.some((opt) => opt.value === value && opt.children)) {
      const childOptions = getAllChildOptions(value);
      return childOptions.every((child) => currentSports.includes(child));
    }

    return currentSports.includes(value);
  };

  const toggleParentExpand = (parentValue, e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedParent(expandedParent === parentValue ? null : parentValue);
  };

  const handleParentCheckboxClick = (option, e) => {
    e.stopPropagation();
    const childOptions = getAllChildOptions(option.value);
    const allChildrenSelected = childOptions.every((child) =>
      currentSports.includes(child)
    );

    let newValues;
    if (allChildrenSelected) {
      newValues = currentSports.filter((v) => !childOptions.includes(v));
    } else {
      newValues = [
        ...currentSports.filter((v) => !childOptions.includes(v)),
        ...childOptions,
      ];
    }

    if (!canAddMore(newValues)) {
      setLimitReached(true);
      message.warning(`You can only select up to ${freeAutoSchedule} leagues. Please upgrade to Premium for unlimited leagues.`);
      return;
    }

    setLimitReached(false);
    setSport(newValues.join(", "));
  };

  const renderOptions = () => {
    return SPORTS_OPTIONS.map((option) => {
      if (option.value === "All") {
        const allSelected = isOptionSelected("All");
        const someSelected = getSomeLeaguesSelected(currentSports);

        return (
          <Option key="All" value="All" label="All">
            <div className="flex items-center">
              <Checkbox
                indeterminate={someSelected && !allSelected}
                checked={allSelected}
                className="mr-2"
                disabled={
                  !isSubscribed && freeAutoSchedule < getAllLeagueValues().length
                }
              />
              <span>All</span>
            </div>
          </Option>
        );
      }

      if (option.children) {
        const allChildrenSelected = isOptionSelected(option.value);
        const someChildrenSelected = option.children.some((child) =>
          isOptionSelected(child.value)
        );

        // Disable parent if selecting all its children would exceed the limit for non-subscribed users
        const allLeagues = getAllLeagueValues();
        const currentCount = currentSports.filter((v) =>
          allLeagues.includes(v)
        ).length;
        const childrenNotSelected = option.children.filter(
          (child) => !currentSports.includes(child.value)
        );
        const wouldExceed =
          !isSubscribed &&
          currentCount + childrenNotSelected.length > freeAutoSchedule;

        return (
          <React.Fragment key={option.value}>
            <Option
              value={option.value}
              label={option.label}
              children={option.children.map((child) => ({
                value: child.value,
                label: child.label,
              }))}
            >
              <div
                className="ant-select-item-option-content flex items-center justify-between w-full"
                onClick={(e) => toggleParentExpand(option.value, e)}
              >
                <div className="flex items-center flex-grow">
                  <Checkbox
                    indeterminate={someChildrenSelected && !allChildrenSelected}
                    checked={allChildrenSelected}
                    onClick={(e) => handleParentCheckboxClick(option, e)}
                    className="mr-2"
                    disabled={wouldExceed && !allChildrenSelected}
                  />
                  <span className="font-medium flex-grow">{option.label}</span>
                </div>
                {expandedParent === option.value ? (
                  <DownOutlined className="ml-2" />
                ) : (
                  <RightOutlined className="ml-2" />
                )}
              </div>
            </Option>
            {expandedParent === option.value &&
              option.children.map((child) => {
                // Disable child if already at limit and not selected for non-subscribed users
                const isSelected = isOptionSelected(child.value);
                const atLimit =
                  !isSubscribed &&
                  selectedLeaguesCount >= freeAutoSchedule &&
                  !isSelected;
                return (
                  <Option
                    key={child.value}
                    value={child.value}
                    label={child.label}
                  >
                    <div className="flex items-center pl-6">
                      <Checkbox
                        checked={isSelected}
                        className="mr-2"
                        disabled={atLimit}
                      />
                      <span>{child.label}</span>
                    </div>
                  </Option>
                );
              })}
          </React.Fragment>
        );
      }

      // For single sport (not parent, not "All")
      // Disable if at limit and not selected for non-subscribed users
      const allLeagues = getAllLeagueValues();
      const isSelected = isOptionSelected(option.value);
      const atLimit =
        !isSubscribed &&
        selectedLeaguesCount >= freeAutoSchedule &&
        !isSelected &&
        allLeagues.includes(option.value);

      return (
        <Option key={option.value} value={option.value} label={option.label}>
          <div className="flex items-center">
            <Checkbox
              checked={isSelected}
              className="mr-2"
              disabled={atLimit}
            />
            <span>{option.label}</span>
          </div>
        </Option>
      );
    });
  };

  return (
    <>
      <div className="mt-3">
        {/* Schedule Information Alert */}
        <div className="mb-4">
          <Alert
            message="Please note: Schedules are posted daily between 4:00 AM and 5:00 AM according to the time zone you selected."
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
            className="bg-blue-50 border-blue-200"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col justify-between text-left">
            <Typography.Text>Channel ID</Typography.Text>
            <Input
              placeholder="Enter channel id"
              prefix={
                <img
                  src={step === "discord" ? Discord : Telegram}
                  alt="Platform"
                  className="h-3 w-full"
                />
              }
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
            />
          </div>
          <div className="text-left">
            <div className="flex items-center mb-1 gap-2">
              <Typography.Text>Sports</Typography.Text>
              {!isSubscribed && (
                <>
                  <span style={{ color: "#E61F26", fontSize: 12 }}>
                    (Max {freeAutoSchedule})
                  </span>
                  <button
                    onClick={handleUpgradeClick}
                    className="inline-flex items-center gap-1 bg-gradient-to-r from-[#e61f26] to-[#ff4757] text-white text-xs px-2 py-1 rounded-full hover:scale-105 transition-transform"
                    title="Upgrade to Premium for unlimited leagues"
                  >
                    <CrownOutlined className="text-xs" />
                    <span>Upgrade</span>
                  </button>
                </>
              )}
            </div>
            <Select
              mode="multiple"
              showSearch
              className="w-full fixed-height-select"
              placeholder="Select sport(s)"
              value={currentSports}
              onChange={handleSportChange}
              onSearch={handleSearch}
              optionLabelProp="label"
              optionFilterProp="label"
              maxTagCount="responsive"
              maxTagTextLength={10}
              dropdownStyle={{ width: 300 }}
              filterOption={(input, option) => {
                const searchText = input.toLowerCase();

                if (
                  option.label &&
                  option.label.toLowerCase().includes(searchText)
                ) {
                  return true;
                }

                const parentOption = SPORTS_OPTIONS.find(
                  (opt) => opt.value === option.value
                );
                if (parentOption && parentOption.children) {
                  return parentOption.children.some((child) =>
                    child.label.toLowerCase().includes(searchText)
                  );
                }

                return false;
              }}
              dropdownRender={(menu) => <div>{menu}</div>}
              style={{ height: "32px", overflow: "hidden" }}
              tagRender={(props) => (
                <Tag
                  {...props}
                  style={{
                    maxWidth: "100px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {props.label}
                </Tag>
              )}
              // [PTG_ONE_LEAGUE_LIST] also disabled until the league list has
              // loaded: against an empty list, "All" would clear the selection.
              disabled={(freeAutoSchedule === 0 && !isSubscribed) || !optionsReady}
              loading={!optionsReady && !optionsError}
            >
              {renderOptions()}
            </Select>
            {optionsError && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                Could not load the league list. Refresh the page to try again.
              </Typography.Text>
            )}
          </div>
        </div>

        <div className="mt-4 text-left">
          <Typography.Text>Channel Description</Typography.Text>
          <Input
            placeholder="Enter channel description (can be anything)"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
          />
        </div>
        {!isEditing && (
          <div className="mt-4 text-left">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addChannel}
              className="bg-[#E61F26] hover:bg-[#C91A20]"
            >
              Add Channel
            </Button>
          </div>
        )}
      </div>
      {showUpgradeModal &&
        <UpgradeModal 
          visible={showUpgradeModal}
          onClose={handleUpgradeClose}
        />
      }
    </>
  );
};

export default AddChannelForm;